import {
  addedWeightProgress,
  suggestedAddedWeight,
  fold,
  lastPerformance,
  planWorkout,
  reduce,
  replaySession,
  results,
  view,
  type RepwiseEvent,
  type SessionAction,
  type SessionState,
  type MainLift,
  type Unit,
  isMainLift,
  lightenable,
  loadFor,
} from '@repwise/domain';
import { uuidv7 } from '@repwise/sync';
import { useEffect, useMemo, useState } from 'react';
import { diag, timed } from '../diag/log.ts';
import { breadcrumb, jankProbe } from '../diag/monitor.ts';
import { AllExercises } from '../components/AllExercises.tsx';
import { PlateStrip, WeightButton } from '../components/PlateSheet.tsx';
import { GuideSheet, useGuides } from '../components/GuideSheet.tsx';
import { Button, Circles, RestRing } from '../components/ui.tsx';
import { mmss, sideText, spokenWt, wt } from '../lib/copy.ts';
import { LIFT_NAME } from '../lib/defaults.ts';
import { useHostedPlan } from '../lib/plan.ts';
import { useNow, useRepo, useWakeLock } from '../lib/hooks.ts';
import { cancelRestAlert, scheduleRestAlert } from '../lib/push.ts';

const ids = (n: number) => Array.from({ length: n }, () => uuidv7());
/** Hosted plans name each set's job (ADR-0020); plain work sets need no label. */
const ROLE: Record<string, string> = { top: 'top set', backoff: 'back-off', ramp: 'build-up', assistance: '', work: '' };

/**
 * The live workout (S02–S11). State is rebuilt from the event log on mount (resume after a kill is exact),
 * then each action updates local state immediately and appends its events: the screen never waits on I/O.
 */
export function Session({ workoutId, events }: { workoutId: string; events: RepwiseEvent[] }) {
  const repo = useRepo();
  const { store } = repo;
  const [state, setState] = useState<SessionState | undefined>(() => replaySession(events, workoutId));
  const [reps, setReps] = useState<number | null>(null);
  const [menu, setMenu] = useState(false);
  const [lighten, setLighten] = useState(false);
  const [overview, setOverview] = useState(false);
  const [guide, setGuide] = useState<string | null>(null);
  const guides = useGuides();
  /** BW01: weight added to a bodyweight exercise (belt), per exercise; defaults to what this workout last used. */
  const [added, setAdded] = useState<Record<string, number | null>>({});
  const now = useNow();
  useWakeLock(true);
  useEffect(() => jankProbe(), []); // long frames during a workout (the iPhone stand-in for LoAF)
  if (!state) return <main className="p-5">Can't find this workout.</main>;
  const unit = state.gym.unit; // the unit this workout was planned in (ADR-0018)
  const kg = (m: number | null | undefined) => wt(m, unit);

  const v = view(state, now);
  const dispatch = (a: SessionAction) => {
    // ADR-0024: what the lifter did, as codes and numbers (no notes)
    const cur = view(state, Date.now()).slot;
    breadcrumb(`session ${a.type}${cur ? ` ${cur.exercise}#${cur.index}` : ''}`);
    diag('session', a.type, { ex: cur?.exercise ?? null, kind: cur?.kind ?? null, set: cur ? cur.index + 1 : null, reps: 'reps' in a ? (a.reps ?? null) : null, events: events.length });
    const step = timed('session', 'reduce', () => reduce(state, a));
    setState(step.state);
    setReps(null);
    void store.append(step.events);
    syncRestAlert(state, step.state);
  };
  /** Keep the Worker's lock-screen alarm in step with the rest on screen (ADR-0006). */
  const syncRestAlert = (prev: SessionState, next: SessionState) => {
    const r = next.rest;
    const p = prev.rest;
    const changed = r && (!p || p.startedAt !== r.startedAt || p.baseS + p.extraS !== r.baseS + r.extraS);
    if (changed && next.status === 'active') {
      const remaining = Math.round((r.startedAt + Math.max(0, r.baseS + r.extraS) * 1000 - Date.now()) / 1000);
      const v2 = view(next, Date.now());
      const nx = v2.slot;
      const body = nx ? `${LIFT_NAME[nx.exercise]}: ${nx.kind === 'warmup' ? 'warm-up' : `set ${nx.index + 1} of ${nx.of}`}${nx.weight === null ? '' : `, ${kg(nx.weight)}`} × ${nx.reps}` : 'Time for your next set';
      scheduleRestAlert(repo, remaining, body);
    } else if ((p && !r) || next.status !== 'active') cancelRestAlert(repo);
  };
  const at = () => Date.now();
  const slot = v.slot;
  // "Squat · 1 of 3": main lifts only; accessories read "Accessory 1 of 2".
  const mains = state.prescriptions.filter((p) => p.exercise !== 'curl' && p.exercise !== 'pushup');
  const accs = state.prescriptions.filter((p) => p.exercise === 'curl' || p.exercise === 'pushup');
  const isAcc = slot?.kind === 'accessory' || (slot?.kind === 'extra' && accs.some((p) => p.exercise === slot.exercise));
  const group = isAcc ? accs : mains;
  const exerciseIdx = slot ? group.findIndex((p) => p.exercise === slot.exercise) : -1;
  // The screen re-renders 4×/s for the timers: anything that scans the log is memoised (ADR-0024 render cost)
  const lastEx = slot && isMainLift(slot.exercise) ? slot.exercise : null;
  const last = useMemo(() => (lastEx ? lastPerformance(events, lastEx) : undefined), [events, lastEx]);
  const suggested = useMemo(() => (slot && slot.weight === null ? { on: addedWeightProgress(events, slot.exercise), w: suggestedAddedWeight(events, slot.exercise, unit === 'kg' ? 250 : 500) } : null), [events, slot?.exercise, slot?.weight, unit]); // eslint-disable-line react-hooks/exhaustive-deps
  const target = reps ?? slot?.reps ?? 5;
  const bodyweight = !!slot && slot.weight === null;
  const lastAdded = slot ? [...state.order].reverse().map((id) => state.slots.find((x) => x.id === id)).find((x) => x?.exercise === slot.exercise && state.log[x.id]?.weight != null) : undefined;
  const progressOn = !!suggested?.on;
  // J26: this workout's own choice first, then what it last used, then (opted in) last time's + a step after every rep
  const addedKg = slot && bodyweight ? (slot.exercise in added ? added[slot.exercise]! : lastAdded ? state.log[lastAdded.id]!.weight : (suggested?.w ?? null)) : null;
  const perHand = !!slot && !!state.prescriptions.find((p) => p.exercise === slot.exercise)?.perHand;
  const setLabel = (x: NonNullable<typeof slot>) =>
    x.kind === 'warmup' ? `Warm-up ${x.index + 1} of ${x.of}` : x.kind === 'extra' ? `Extra set ${x.index + 1}` : `Set ${x.index + 1} of ${x.of}${x.role && ROLE[x.role] ? ` · ${ROLE[x.role]}` : ''}${x.weight === null ? ` · ${x.reps} reps` : ''}`;
  // S03b: hosted-plan exercises list every set with its own weight and role; the built-in keeps the circles.
  const presc = slot ? state.prescriptions.find((p) => p.exercise === slot.exercise) : undefined;
  const planned = slot && presc?.setPlan && slot.kind === 'work' ? presc.setPlan[slot.index] : undefined;
  const setList =
    slot && presc?.setPlan && slot.kind === 'work'
      ? {
          top: presc.setPlan.find((x) => x.kind === 'top')?.weight ?? presc.weight,
          // the weight of the set before this one (the plate-change chip "75 kg → 55 kg")
          prev: (() => {
            const own = state.slots.filter((x) => x.exercise === slot.exercise && x.kind === 'work');
            const i = own.findIndex((x) => x.id === slot.id);
            return i > 0 ? own[i - 1]!.weight : null;
          })(),
          rows: state.slots
            .filter((x) => x.exercise === slot.exercise && x.kind === 'work')
            .map((x, i, all) => {
              const done = x.id in state.log;
              const nowIdx = all.findIndex((y) => !(y.id in state.log));
              return { id: x.id, n: x.index + 1, weight: x.weight, reps: x.reps, role: x.role ? ROLE[x.role] : '', state: done ? 'done' : i === nowIdx ? 'now' : i === nowIdx + 1 ? 'next' : 'todo' };
            }),
        }
      : null;

  return (
    <main className="min-h-full flex flex-col max-w-[480px] mx-auto" style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + ${v.canUndo && v.phase !== 'rest' ? 220 : 160}px)` }}>
      <header className="flex items-center justify-between px-5 h-14">
        <span className="text-callout">{slot ? `${LIFT_NAME[slot.exercise]} · ${exerciseIdx + 1} of ${group.length}` : `Workout ${state.kind}`}</span>
        <span className="flex items-center gap-3">
          <span className="num text-text-muted" aria-label="Elapsed">
            {mmss(v.elapsedS)}
          </span>
          <button type="button" className="min-w-11 min-h-11" aria-label="Workout menu" onClick={() => setMenu((m) => !m)}>
            ⋯
          </button>
        </span>
      </header>

      {menu && (
        <div className="mx-5 mb-3 bg-surface-overlay rounded-[14px] p-3 space-y-2" role="menu">
          <Button kind="secondary" onClick={() => (setMenu(false), setOverview(true))}>
            All exercises
          </Button>
          {v.slot && (
            <Button kind="secondary" onClick={() => (setMenu(false), setGuide(v.slot!.exercise))}>
              How to do {LIFT_NAME[v.slot.exercise]}
            </Button>
          )}
          {!state.lightened && lightenable(state).length > 0 && (
            <Button kind="secondary" onClick={() => (setMenu(false), setLighten(true))}>
              Go lighter today
            </Button>
          )}
          <Button kind="secondary" onClick={() => (setMenu(false), dispatch({ type: 'SKIP_EXERCISE', ids: ids(20), at: at() }))}>
            Skip exercise
          </Button>
          <Button kind="secondary" onClick={() => (setMenu(false), dispatch({ type: 'END_EARLY', id: uuidv7(), at: at() }))}>
            End workout early
          </Button>
          <Button kind="text" onClick={() => (setMenu(false), dispatch({ type: 'CANCEL', id: uuidv7(), at: at() }))}>
            Cancel workout
          </Button>
        </div>
      )}

      {lighten && !state.lightened && (
        // ADR-0025 (FB-003): not feeling well. The rest of today, lighter; those lifts hold their weight next time.
        <div className="mx-5 mb-3 bg-surface-overlay rounded-[14px] p-4 space-y-3" role="group" aria-label="Go lighter today">
          <h2 className="text-headline">Lighter for the rest of today</h2>
          <p className="text-sm text-text-muted">Sets you've done keep their weight. The lifts you lighten stay at today's planned weight next time: no increase, and it doesn't count as a miss.</p>
          <div className="grid grid-cols-2 gap-2">
            {[0.1, 0.2].map((f) => (
              <Button key={f} kind="secondary" onClick={() => (setLighten(false), dispatch({ type: 'LIGHTEN', id: uuidv7(), at: at(), fraction: f }))}>
                {Math.round(f * 100)}% lighter
              </Button>
            ))}
          </div>
          <Button kind="text" onClick={() => setLighten(false)}>
            Keep today's weights
          </Button>
        </div>
      )}
      {state.lightened && (
        <p className="mx-5 mb-2 text-sm text-text-muted" role="status">
          Lighter today (−{Math.round(state.lightened.fraction * 100)}%). Next time stays at today's planned weights.
        </p>
      )}

      {guide && <GuideSheet exercise={guide} guides={guides} onClose={() => setGuide(null)} />}
      {overview && (
        <AllExercises
          state={state}
          onFocus={(exercise) => (setOverview(false), dispatch({ type: 'FOCUS', id: uuidv7(), at: at(), exercise }))}
          onExtra={(exercise) => (setOverview(false), dispatch({ type: 'ADD_EXTRA_SET', id: uuidv7(), at: at(), exercise }))}
          onClose={() => setOverview(false)}
        />
      )}

      <section className="flex-1 px-5">
        {v.phase === 'transition' && v.transition && (
          <div className="mt-6 space-y-4">
            {v.transition.done && (
              <p className="text-headline">
                {LIFT_NAME[v.transition.done.lift]} done, {v.transition.done.reps.filter((r) => r !== null).join(' ')} at {kg(v.transition.done.weight)}
              </p>
            )}
            <p className="text-text-muted">Next</p>
            <p className="num text-num-l">
              {LIFT_NAME[v.transition.next.exercise]}, {v.transition.next.weight === null ? 'bodyweight' : kg(v.transition.next.weight)}
            </p>
            {v.loading?.delta && (v.loading.delta.remove.length > 0 || v.loading.delta.add.length > 0) && (
              <p>
                Change plates: {v.loading.delta.remove.length ? `remove ${sideText(v.loading.delta.remove)}` : ''}
                {v.loading.delta.remove.length && v.loading.delta.add.length ? ', ' : ''}
                {v.loading.delta.add.length ? `load ${sideText(v.loading.delta.add)}` : ''} each side
              </p>
            )}
          </div>
        )}

        {v.phase === 'rest' && v.rest && (
          <div className="mt-4 space-y-5">
            <RestRing rest={v.rest} />
            {slot && (
              <p className="text-center">
                Next: {slot.kind === 'warmup' ? 'warm-up' : slot.kind === 'extra' ? 'extra set' : `set ${slot.index + 1} of ${slot.of}`}, <span className="num">{slot.weight === null ? `${slot.reps} reps` : `${kg(slot.weight)} × ${slot.reps}`}</span>
              </p>
            )}
            {v.lastLogged && (
              <p className="text-center text-text-muted">
                Set {v.lastLogged.slot.index + 1}: {v.lastLogged.entry.reps} reps{' '}
                <button type="button" className="text-now-ink underline min-h-11 px-2" onClick={() => dispatch({ type: 'EDIT', id: uuidv7(), at: at(), slotId: v.lastLogged!.slot.id, reps: Math.max(0, (v.lastLogged!.entry.reps ?? 0) - 1) })}>
                  One fewer
                </button>
              </p>
            )}
            {v.canUndo && (
              <div className="flex justify-center" role="status">
                <button type="button" className="bg-surface-overlay rounded-full px-4 min-h-11" onClick={() => dispatch({ type: 'UNDO', id: uuidv7(), at: at() })}>
                  Logged · Undo ({Math.ceil(v.undoRemainingMs / 1000)})
                </button>
              </div>
            )}
            <div className="flex gap-3">
              <Button kind="secondary" onClick={() => dispatch({ type: 'REST_ADD', id: uuidv7(), at: at(), seconds: 30 })}>
                +30 s
              </Button>
              <Button kind="secondary" onClick={() => dispatch({ type: 'REST_SKIP', id: uuidv7(), at: at() })}>
                Skip rest
              </Button>
            </div>
          </div>
        )}

        {(v.phase === 'warmup' || v.phase === 'work' || v.phase === 'accessory') && slot && (
          <div className="mt-6 text-center space-y-4">
            <div>
              {slot.barbell && slot.weight !== null ? (
                <WeightButton gym={state.gym} weight={slot.weight} className="decoration-transparent">
                  <span className="num text-num-hero">{(slot.weight / 100).toString()}</span>
                </WeightButton>
              ) : (
                <span className="num text-num-hero">{slot.weight === null ? target : (slot.weight / 100).toString()}</span>
              )}
              <span className="num text-num-l text-text-muted ml-2">{slot.weight === null ? 'reps' : unit}</span>
              {perHand && <span className="block text-sm text-text-muted">per hand</span>}
            </div>
            {bodyweight && slot.kind !== 'warmup' && (
              <AddedWeight unit={unit} value={addedKg} onChange={(v) => setAdded((a) => ({ ...a, [slot.exercise]: v }))} progress={progressOn} onProgress={(on) => void store.append([{ type: 'added_weight_progress', id: uuidv7(), at: Date.now(), exercise: slot.exercise, on }])} />
            )}
            <p className="text-text-muted">
              {slot.kind === 'warmup' ? `Warm-up ${slot.index + 1} of ${slot.of} · ${slot.reps} reps` : slot.kind === 'extra' ? `Extra set ${slot.index + 1} · doesn't count towards progression` : slot.role && ROLE[slot.role] ? `Set ${slot.index + 1} of ${slot.of} · ${ROLE[slot.role]} · ${slot.reps} reps` : `${slot.reps} reps`}
            </p>
            {planned?.target !== undefined && slot.weight !== null && (
              <p className="text-xs text-text-muted">
                Plan {wt(planned.target, unit)}; your plates make {wt(slot.weight, unit)}.
              </p>
            )}
            {last && slot.kind === 'work' && (
              <p className="text-text-muted text-sm">
                Last time: {wt(last.weight, last.unit)}, {last.reps.map((r) => r ?? '–').join(' ')}
              </p>
            )}
            {slot.kind !== 'warmup' && !setList && <Circles circles={v.circles} />}
            {v.loading && !(setList && slot.weight !== null && slot.barbell) && (
              <p className="text-sm">
                {v.loading.delta && v.loading.delta.remove.length > 0 ? (
                  // S03b: say what to do with the plates when some come off (engine-data loading table)
                  <>
                    Each side: <span className="num">take off {sideText(v.loading.delta.remove)}{v.loading.delta.add.length ? `, put on ${sideText(v.loading.delta.add)}` : ''}</span>
                  </>
                ) : (
                  <>
                    Each side: <span className="num">{sideText([...(v.loading.delta?.keep ?? []), ...(v.loading.delta?.add ?? [])])}</span>
                  </>
                )}
              </p>
            )}
            {setList && slot.weight !== null && slot.barbell && v.loading && (
              // S03b final (Wave 2.3): a plate-change card, the change from the previous set, then the bar as loaded
              <section className="text-left bg-surface-raised rounded-[16px] p-4" aria-label="Plate change">
                <div className="flex justify-between items-baseline gap-3">
                  <h2 className="text-headline">Plate change</h2>
                  {setList.prev !== null && setList.prev !== slot.weight && (
                    <span className="num text-xs rounded-full bg-surface-sunken px-2 py-1 text-text-muted">
                      {wt(setList.prev, unit)} → {wt(slot.weight, unit)}
                    </span>
                  )}
                </div>
                <p className="text-sm mt-1">
                  Each side:{' '}
                  {v.loading.delta && (v.loading.delta.remove.length > 0 || v.loading.delta.add.length > 0) ? (
                    <span className="num">
                      {v.loading.delta.remove.length > 0 && <span className="text-miss">take off {sideText(v.loading.delta.remove)}</span>}
                      {v.loading.delta.remove.length > 0 && v.loading.delta.add.length > 0 ? ', ' : ''}
                      {v.loading.delta.add.length > 0 && <span className="text-success">put on {sideText(v.loading.delta.add)}</span>}
                    </span>
                  ) : (
                    <span className="num">{v.loading.delta ? 'no change' : sideText(loadFor(state.gym, slot.weight) ?? []) || 'just the bar'}</span>
                  )}
                </p>
                <div className="mt-3">
                  <PlateStrip gym={state.gym} weight={slot.weight} />
                </div>
              </section>
            )}
            {setList && (
              <section className="text-left mt-2 bg-surface-raised rounded-[16px] p-4" aria-label={`Sets for ${LIFT_NAME[slot.exercise]}`}>
                <h2 className="text-headline">Sets for {LIFT_NAME[slot.exercise]}</h2>
                <ol className="mt-3 space-y-2">
                  {setList.rows.map((row) => (
                    <li key={row.id} aria-current={row.state === 'now' ? 'step' : undefined} className={`flex items-center justify-between gap-3 rounded-[12px] px-3 py-2 min-h-11 ${row.state === 'now' ? 'bg-now text-on-now' : 'bg-surface-sunken'}`}>
                      <span className="flex items-center gap-3">
                        <span className={`num w-7 h-7 rounded-full grid place-items-center text-sm shrink-0 ${row.state === 'now' ? 'bg-on-now text-now' : 'bg-surface-raised text-text-muted'}`}>{row.n}</span>
                        <span className="flex flex-col">
                          <span className="num font-medium">
                            {row.weight === null ? 'Bodyweight' : wt(row.weight, unit)} × {row.reps}
                          </span>
                          {row.role && <span className={`text-xs ${row.state === 'now' ? '' : 'text-text-muted'}`}>{row.role[0]!.toUpperCase() + row.role.slice(1)}</span>}
                        </span>
                      </span>
                      <span className={`text-sm font-medium shrink-0 ${row.state === 'done' ? 'text-success' : row.state === 'now' ? 'rounded-full bg-on-now text-now px-2 py-0.5' : 'text-text-muted'}`}>
                        {row.state === 'done' ? '✓ Done' : row.state === 'now' ? 'Now' : row.state === 'next' ? 'Next' : ''}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            {slot.kind === 'warmup' && (
              <Button kind="text" onClick={() => dispatch({ type: 'SKIP_WARMUPS', ids: ids(10), at: at() })}>
                Skip warm-up
              </Button>
            )}
          </div>
        )}

        {v.phase === 'finish' && <Finish state={state} events={events} onDone={(extra) => (extra.length && void store.append(extra), dispatch({ type: 'FINISH', id: uuidv7(), at: at() }))} />}
      </section>


      <div className="fixed inset-x-0 bottom-0 p-5 bg-surface" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}>
        <div className="max-w-[480px] mx-auto">
          {/* outside rest, the Undo chip sits in the bottom panel, above the Set Bar (phone test, 14 Oct); during rest
              it's in the page beside the last set, so it never covers the rest controls */}
          {v.canUndo && v.phase !== 'rest' && (
            <div className="flex justify-center mb-2" role="status">
              <button type="button" className="bg-surface-overlay rounded-full px-4 min-h-11" onClick={() => dispatch({ type: 'UNDO', id: uuidv7(), at: at() })}>
                Logged · Undo ({Math.ceil(v.undoRemainingMs / 1000)})
              </button>
            </div>
          )}
          {v.phase === 'transition' ? (
            <Button onClick={() => dispatch({ type: 'CONTINUE', id: uuidv7(), at: at() })}>Start {state.slots.find((s) => s.id === slot?.id)?.kind === 'warmup' ? 'warm-up' : 'next exercise'}</Button>
          ) : slot && v.phase !== 'finish' ? (
            <SetBar
              label={setLabel(slot)}
              weight={bodyweight ? addedKg : slot.weight}
              bodyweight={bodyweight}
              perHand={perHand}
              unit={unit}
              reps={target}
              onLog={() => dispatch({ type: 'LOG', id: uuidv7(), at: at(), reps: target, ...(bodyweight && addedKg ? { weight: addedKg } : {}) })}
              onMinus={() => setReps(Math.max(0, target - 1))}
            />
          ) : null}
        </div>
      </div>
    </main>
  );
}

/** C1 Set Bar: the whole bar logs; "−" is the button equivalent of the swipe (WCAG 2.5.1). */
function SetBar({ label, weight, unit, reps, onLog, onMinus, bodyweight, perHand }: { label: string; weight: number | null; unit: Unit; reps: number; onLog: () => void; onMinus: () => void; bodyweight?: boolean; perHand?: boolean }) {
  const load = weight === null ? '' : `${bodyweight ? 'BW + ' : ''}${weight / 100} ${unit}${perHand ? ' each' : ''} × `;
  return (
    <div className="flex gap-3 items-stretch">
      <button type="button" className="w-16 rounded-[20px] border border-outline text-2xl" onClick={onMinus} aria-label="One fewer rep">
        −
      </button>
      <button
        type="button"
        onClick={onLog}
        className="flex-1 min-w-0 min-h-16 rounded-[20px] bg-action text-on-action flex flex-col justify-center gap-0.5 px-5 py-2 text-left active:scale-[0.98] transition-transform"
        aria-label={`${label}, ${weight === null ? (bodyweight ? 'bodyweight, ' : '') : `${bodyweight ? 'bodyweight plus ' : ''}${spokenWt(weight, unit)}${perHand ? ' per hand' : ''}, `}${reps} reps. Double-tap to log.`}
      >
        {/* Two rows so larger text sizes (200%) and narrow phones never crush the weight (Wave 2.4 accessibility pass) */}
        <span className="flex items-baseline justify-between gap-3 w-full">
          <span className="text-sm min-w-0">{label}</span>
          <span className="font-semibold shrink-0">Log</span>
        </span>
        <span className="num text-num-l leading-tight [overflow-wrap:anywhere]">
          {load}
          {reps}
        </span>
      </button>
    </div>
  );
}

/** BW01: reps only by default; "+ Added weight" for a belt. Logged as the set's weight (the extra load only). */
function AddedWeight({ unit, value, onChange, progress, onProgress }: { unit: Unit; value: number | null; onChange: (v: number | null) => void; progress: boolean; onProgress: (on: boolean) => void }) {
  const step = unit === 'kg' ? 250 : 500;
  const toggle = (
    <label className="flex items-center justify-center gap-2 text-sm text-text-muted min-h-11">
      <input type="checkbox" checked={progress} onChange={(e) => onProgress(e.target.checked)} />
      Add {wt(step, unit)} next time when I get every rep
    </label>
  );
  if (value === null)
    return (
      <div>
        <p className="text-sm text-text-muted">
          No added weight.{' '}
          <button type="button" className="text-now-ink underline min-h-11 px-2" onClick={() => onChange(step * 2)}>
            + Added weight
          </button>
        </p>
        {progress && toggle}
      </div>
    );
  return (
    <div className="flex flex-wrap items-center justify-center gap-3" role="group" aria-label="Added weight">
      <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" aria-label="Less added weight" onClick={() => onChange(value - step > 0 ? value - step : null)}>
        −
      </button>
      <span className="num">
        + {wt(value, unit)}
      </span>
      <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" aria-label="More added weight" onClick={() => onChange(value + step)}>
        +
      </button>
      <button type="button" className="text-text-muted underline min-h-11 px-2 text-sm" onClick={() => onChange(null)}>
        Bodyweight only
      </button>
      <div className="basis-full">{toggle}</div>
    </div>
  );
}

/** S07: quiet finish — results, one progress line, next workout. */
function Finish({ state, events, onDone }: { state: SessionState; events: RepwiseEvent[]; onDone: (extra: RepwiseEvent[]) => void }) {
  const kg = (m: number | null | undefined) => wt(m, state.gym.unit);
  const [note, setNote] = useState('');
  const [bw, setBw] = useState('');
  const [liftNotes, setLiftNotes] = useState<Record<string, string>>({});
  const [showLiftNotes, setShowLiftNotes] = useState(false);
  const bwValue = Number(bw.replace(',', '.'));
  const bwOk = bw.trim() === '' || (Number.isFinite(bwValue) && bwValue >= 20 && bwValue <= 400);
  const finish = () => {
    const now = Date.now();
    const extra: RepwiseEvent[] = [];
    if (note.trim()) extra.push({ type: 'workout_noted', id: uuidv7(), at: now, workoutId: state.workoutId, note: note.trim().slice(0, 1000) });
    for (const [ex, n] of Object.entries(liftNotes)) if (n.trim()) extra.push({ type: 'workout_noted', id: uuidv7(), at: now, workoutId: state.workoutId, exercise: ex as MainLift, note: n.trim().slice(0, 500) });
    if (bw.trim() && bwOk) extra.push({ type: 'bodyweight_logged', id: uuidv7(), at: now, workoutId: state.workoutId, weight: Math.round(bwValue * 100), unit: state.gym.unit });
    onDone(extra);
  };
  // Recomputed only when the log or the workout changes, not on every timer tick (ADR-0024 render cost)
  const { rs, after, programmeAfter } = useMemo(() => {
    const after = [...events, { type: 'workout_finished', id: 'preview', at: Date.now(), workoutId: state.workoutId, endedEarly: false } as RepwiseEvent];
    return { rs: results(state), after, programmeAfter: fold(after) };
  }, [events, state]);
  // A hosted plan's next workout comes from the plan engine, not the built-in A/B (found 14 Oct 2026: Ultra Max showed 5×5 A)
  const hosted = useHostedPlan(after, programmeAfter);
  const next = useMemo(() => {
    if (hosted.selection) return hosted.live ? { kind: hosted.live.next.workout, prescriptions: hosted.live.next.prescriptions } : null;
    return planWorkout(programmeAfter, Date.now(), undefined, Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, [hosted.selection, hosted.live, programmeAfter]);
  // Bodyweight lifts (dips, chin-ups) don't drive progression, so results() leaves them out; the summary still shows them
  const bodyweight = state.prescriptions
    .filter((p) => p.weight === null && !rs.some((r) => r.lift === p.exercise))
    .map((p) => {
      const slots = state.slots.filter((x) => x.exercise === p.exercise && x.kind === 'work');
      const reps = slots.map((x) => state.log[x.id]?.reps ?? null);
      return { lift: p.exercise, reps, outcome: reps.every((r, i) => r !== null && r >= slots[i]!.reps) ? 'success' : 'miss' };
    })
    .filter((b) => b.reps.length && b.reps.some((r) => r !== null));
  const order = (lift: string) => state.prescriptions.findIndex((p) => p.exercise === lift);
  const rows = [...rs.map((r) => ({ ...r, load: kg(r.weight) })), ...bodyweight.map((b) => ({ ...b, load: 'Bodyweight' }))].sort((a, b) => order(a.lift) - order(b.lift)); // the workout's order
  const work = rows.reduce((n, r) => n + r.reps.length, 0);
  const done = rs.reduce((n, r) => {
    const target = state.prescriptions.find((p) => p.exercise === r.lift)?.reps ?? 5;
    return n + r.reps.filter((x) => x !== null && x >= target).length;
  }, 0) + bodyweight.reduce((n, b) => n + b.reps.filter((x, i) => x !== null && x >= state.slots.filter((y) => y.exercise === b.lift && y.kind === 'work')[i]!.reps).length, 0);
  return (
    <div className="mt-6 space-y-4">
      <h1 className="text-title num">Workout {state.kind} done</h1>
      <p className="text-text-muted">
        {mmss(Math.floor((Date.now() - state.startedAt) / 1000))} · {done} of {work} working sets
      </p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.lift} className="flex justify-between bg-surface-raised rounded-[14px] p-3">
            <span>{LIFT_NAME[r.lift]}</span>
            <span className="num">
              {r.load} · {r.reps.map((x) => x ?? '–').join(' ')} {r.outcome === 'success' ? '✓' : ''}
            </span>
          </li>
        ))}
      </ul>
      {next && (
        <>
          <p className="text-text-muted">Next: Workout {next.kind}</p>
          <ul className="text-sm space-y-1">
            {next.prescriptions.map((p) => (
              <li key={p.exercise} className="flex justify-between">
                <span>{LIFT_NAME[p.exercise]}</span>
                <span className="num">{p.weight === null ? 'Bodyweight' : kg(p.weight)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <details className="bg-surface-raised rounded-[14px] p-3" open={showLiftNotes || undefined}>
        <summary className="min-h-11 flex items-center text-sm" onClick={() => setShowLiftNotes((x) => !x)}>
          Add a note or bodyweight (optional)
        </summary>
        <div className="space-y-3 mt-2">
          <label className="block text-sm">
            Note on this workout
            <textarea className="block w-full mt-1 min-h-20 rounded-[10px] bg-surface-sunken border border-outline p-2" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          {rs.map((r) => (
            <label key={r.lift} className="block text-sm">
              Note on {LIFT_NAME[r.lift]}
              <input className="block w-full mt-1 min-h-11 rounded-[10px] bg-surface-sunken border border-outline px-2" maxLength={500} value={liftNotes[r.lift] ?? ''} onChange={(e) => setLiftNotes((x) => ({ ...x, [r.lift]: e.target.value }))} />
            </label>
          ))}
          <label className="block text-sm">
            Bodyweight ({state.gym.unit})
            <input inputMode="decimal" className="block w-28 mt-1 min-h-11 rounded-[10px] bg-surface-sunken border border-outline px-2 num" value={bw} onChange={(e) => setBw(e.target.value)} />
          </label>
          {!bwOk && (
            <p className="text-miss text-sm" role="alert">
              Check the bodyweight.
            </p>
          )}
        </div>
      </details>
      <div className="pt-4">
        <Button disabled={!bwOk} onClick={finish}>
          Done
        </Button>
      </div>
    </div>
  );
}
